import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateOrderLineListHandlers } from '../../../../features/projectManagement/projectManagementSlice'
import useToastr from '../../../../hooks/useToastr'
import ProjectService from '../../../../services/ProjectService'
import { E2EUserPaginatedModel } from '../../../../shared/models/E2EUserPaginatedModel'
import { MailModel } from '../../../../shared/models/Mail.model'
import { useDiscardDraft } from '../components/handler-assignment-notification-email-editor-dialog/useDiscardDraft'
import HandlerType from '../enums/handler-type'
import PmrE2EProjectDetailsOrderlineModel from '../interfaces/pmr-e2e-project-details-orderline-model'

export const useSaveHandler = ({ onSuccess }: { onSuccess: () => void }) => {
  const [isSavingHandler, setIsSavingHandler] = useState<boolean>(false)
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastr()

  const getLabelByHandlerType = (handlerType: HandlerType): string | null => {
    if (handlerType === HandlerType.OrderCreator) return null

    switch (handlerType) {
      case HandlerType.FlexHandler:
        return 'FLEX Handler'
      case HandlerType.GmaWBHandler:
        return 'GMAWB Handler'
      case HandlerType.MilestoneHandler:
        return 'Milestone Handler'
      case HandlerType.TaskHandler:
        return 'Task Handler'
    }
  }

  const saveHandler = async (
    projectLines: PmrE2EProjectDetailsOrderlineModel[],
    handlerType: HandlerType,
    newHandler: E2EUserPaginatedModel,
    customMail?: MailModel
  ) => {
    setIsSavingHandler(true)
    const handlerTypeLabel = getLabelByHandlerType(handlerType)

    const showSuccessMessage = () => {
      if (projectLines.length === 1) {
        showSuccess(
          `${handlerTypeLabel} Updated`,
          `${handlerTypeLabel} for order line <strong>'${projectLines[0].orderLineNumber}'</strong> successfully reassigned to <strong>'${newHandler.displayName}'</strong>.`
        )
      } else {
        showSuccess(
          `${handlerTypeLabel}s Reassigned`,
          `${handlerTypeLabel}s for selected order lines successfully reassigned`
        )
      }
    }

    const showErrorMessage = () => {
      showError(
        `${handlerTypeLabel} Reassignment Failed`,
        `Failed to reassign ${handlerTypeLabel}. Please try again or contact support if issue persists.`
      )
    }

    try {
      const response = await ProjectService.updateProjectLineHandler({
        e2eProjectId: projectLines[0].e2EProjectId!,
        e2eProjectLineId: projectLines.map(line => line.id),
        handlerType,
        handlerInformation: newHandler,
        customMail,
      })

      if (!response.isSuccess) {
        showErrorMessage()
      }

      for (const projectLine of projectLines) {
        dispatch(
          updateOrderLineListHandlers({
            id: projectLine.id,
            projectId: projectLine.e2EProjectId,
            handlerName: newHandler.displayName,
            handlerEmail: newHandler.normalizedEmail,
            handlerType,
          })
        )
      }

      showSuccessMessage()
      onSuccess()
    } catch (error) {
      showErrorMessage()
    } finally {
      setIsSavingHandler(false)
    }
  }

  return {
    isSavingHandler,
    saveHandler,
    getLabelByHandlerType,
  }
}
